import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';

const AuthContext = createContext(null);

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Refresh from server in the background so the role (and any profile
        // change) stays current without forcing a re-login.
        axios.get(`${API_BASE_URL}/api/auth/me`)
          .then((resp) => {
            if (resp.data?.success && resp.data.user) {
              localStorage.setItem(USER_KEY, JSON.stringify(resp.data.user));
              setUser(resp.data.user);
            }
          })
          .catch(() => { /* keep cached user on transient errors */ });
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }

    setLoading(false);
  }, []);

  // Login with a pre-issued JWT token (e.g., passed via URL from iClickPlus)
  const loginWithToken = useCallback(async (token) => {
    setLoading(true);
    setError(null);
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`);
      if (response.data.success) {
        const userData = response.data.user;
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        setUser(userData);
        return { success: true };
      } else {
        delete axios.defaults.headers.common['Authorization'];
        return { success: false, error: 'Token validation failed' };
      }
    } catch (err) {
      delete axios.defaults.headers.common['Authorization'];
      return { success: false, error: err.response?.data?.error || 'Invalid token' };
    } finally {
      setLoading(false);
    }
  }, []);

  // Login with Google credential
  const loginWithGoogle = useCallback(async (credential) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/google`, {
        credential
      });

      if (response.data.success) {
        const { token, user: userData } = response.data;

        // Save to localStorage
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));

        // Set default auth header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setUser(userData);
        return { success: true };
      } else {
        setError(response.data.error);
        return { success: false, error: response.data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      // Notify server (optional, for logging)
      await axios.post(`${API_BASE_URL}/api/auth/logout`);
    } catch (e) {
      // Ignore errors
    }

    // Clear local data
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    delete axios.defaults.headers.common['Authorization'];

    setUser(null);
  }, []);

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Capability check: admins always pass; otherwise look at the resolved
  // permissions map from the server (user.permissions[feature].{view|edit}).
  const can = useCallback((feature, mode = 'view') => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const p = user.permissions?.[feature];
    return !!(p && p[mode === 'edit' ? 'edit' : 'view']);
  }, [user]);

  // Get current token
  const getToken = useCallback(() => {
    return localStorage.getItem(TOKEN_KEY);
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    loginWithGoogle,
    loginWithToken,
    logout,
    getToken,
    setError,
    can
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
